import { Component, HostBinding, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, TemplateRef, ViewChild, inject, DestroyRef, ChangeDetectorRef, ElementRef, AfterViewInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { CreateFlagResponse, MiniUserProfile, UserImageFlag, UserImageResponse, UserImageService } from "./user-image.service";
import { AdaptiveContainerService, AdaptiveDialogRef } from "../adaptive-container/adaptive-container.service";
import { User } from "../../../models/user.model";
import { ServiceStatus } from "../service-status.enum";
import { ProfilesService } from "../../profiles/profiles.service";
import { UserService } from "../../../../services/user-service.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UserPresenceService } from "./user-presence.service";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormField, MatFormFieldModule, MatLabel } from "@angular/material/form-field";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { GridComponent, GridModule } from "@progress/kendo-angular-grid";
import { CustomDatePipe } from "../custom-date/custom-date.pipe";
import { MatMenu, MatMenuItem, MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatInputModule } from "@angular/material/input";
import { OverlayContainer } from "@angular/cdk/overlay";
import { TranslateModule } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { RtI18nDirective } from '../runtime-translation.directive';

@Component({
  imports: [
    RtI18nDirective,
    CommonModule,
    CustomDatePipe,
    GridComponent,
    GridModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormField,
    MatFormFieldModule,
    MatInputModule,
    MatLabel,
    MatMenu,
    MatMenuItem,
    MatMenuModule,
    MatMenuTrigger,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule
  ],
  selector: 'user-image',
  templateUrl: './user-image.component.html',
  styleUrl: './user-image.component.scss'
})
export class UserImageComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  private destroyRef = inject(DestroyRef);
  @HostBinding("style.width") get width(): string {
    return this.size;
  }
  @HostBinding("style.height") get height(): string {
    return this.size;
  }
  @HostBinding("style.display") get display(): string {
    return "block";
  }

  @ViewChild('header') headerRef: TemplateRef<any>;
  @ViewChild('body') bodyRef: TemplateRef<any>;
  @ViewChild('profileDialog') profileDialogTemplate!: TemplateRef<any>;

  @Input() userId: number;
  displayUserId: number;
  @Input() size: string = "100px"; /**Height/width in pixels (or percent) of the picture*/
  @Input() profileEnabled: boolean = true; /**Show the profile on click?*/
  @Input() isCircle: boolean = true; /**Have the border radius be 50%?*/
  @Input() forceShowAvatar: boolean = false; /**Force show avatar regardless of user preference (overrides useAvatar and user preference)*/
  @Input() forceShowProfileImage: boolean = false;  /**Force show profile image only, ignoring avatar preference (overrides useAvatar and user preference)*/
  @Input() showOnlineStatus: boolean = false; /**Show online status indicator (if available)**/
  @Input() schoolId?: number; /**When set (e.g. from a school's directory), resolve a staff member's School/Phone for THIS school instead of their primary location*/

  readonly ServiceStatus = ServiceStatus;
  loadUserImageServiceStatus = ServiceStatus.unset;
  imageUrl: string | null = null;
  avatarUrl: string | null = null;
  private forceFreshImageUrl = false; // when true, the next loaded image/avatar URL gets a one-time cache-buster
  userPreferenceUseAvatar: boolean | null = null; // Loaded from storage
  hasProfileImage: boolean = true;
  initials: string = "";
  currentUser: User | undefined;
  currentUserType = '';
  statusDotSize: any;

  // Modal Stuff
  emailCopied: boolean = false;
  userDataServiceStatus = ServiceStatus.unset;
  getStudentsServiceStatus = ServiceStatus.unset;
  getGuardiansServiceStatus = ServiceStatus.unset;
  getAlternativeContactServiceStatus = ServiceStatus.unset;
  alternativeContact: User | null = null;
  userData: MiniUserProfile | null = null;
  private profileDialogRef?: AdaptiveDialogRef<any>;
  private imageLoadSubscription?: Subscription;

  flagStatus: ServiceStatus = ServiceStatus.unset;
  flags: UserImageFlag[];
  private flagFormDialogRef?: MatDialogRef<any>;
  flagForm: FormGroup;

  canShowEmail: boolean = false;
  canShowPhone: boolean = false;

  @ViewChild('flagHistoryDialog') flagHistoryDialog!: TemplateRef<any>;

  @ViewChild('flagFormDialog') flagFormDialog!: TemplateRef<any>;

  get sizeAsNumber(): number | null {
    const match = this.size.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**Diameter for the loading spinner. `size` may be in em/% (e.g. "1.3em" parses
   * to 1), which would make Material compute a negative <svg> viewBox and throw.
   * Clamp to a valid minimum so the spinner is always renderable.*/
  get spinnerDiameter(): number {
    const n = this.sizeAsNumber;
    return n != null && n >= 16 ? n : 16;
  }

  constructor(
    private dialog: MatDialog,
    private adaptiveContainer: AdaptiveContainerService,
    private userImageService: UserImageService,
    private profileService: ProfilesService,
    private router: Router,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private elementRef: ElementRef,
    private overlayContainer: OverlayContainer,
    private userPresenceService: UserPresenceService,
    private cdr: ChangeDetectorRef
  ) {
  }

  async ngOnInit() {
    this.displayUserId = this.userId;
    this.currentUser = this.userService.CurrentUserProfile;
    await this.initializeComponent();
    this.mapStatusDotSize();
    if (this.showOnlineStatus) {
      this.userPresenceService.addUserPresenceIdToTracking(this.userId);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userId'] && !changes['userId'].firstChange) {
      const previousUserId = changes['userId'].previousValue;
      const currentUserId = changes['userId'].currentValue;

      if (previousUserId !== currentUserId) {
        this.displayUserId = currentUserId;
        this.resetComponentState();
        this.initializeComponent();
      }
    }

    // Trigger change detection when force flags change
    if (changes['forceShowAvatar'] || changes['forceShowProfileImage']) {
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    // Clean up created blob URLs to prevent memory leaks
    try {
      if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.imageUrl);
      }
    } catch (error) {
      console.warn('Error revoking blob URL:', this.imageUrl, error);
    }

    this.userPresenceService.removeUserPresenceIdFromTracking(this.userId);
  }

  ngAfterViewInit(): void {
    
  }
  // #region UserImage Helpers
  private resetComponentState() {
    //Cancel any in-flight image loading subscription
    if (this.imageLoadSubscription) {
      this.imageLoadSubscription.unsubscribe();
      this.imageLoadSubscription = undefined;
    }
    if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(this.imageUrl);
      } catch (error) {
        console.warn('Error revoking blob URL:', this.imageUrl, error);
      }
    }

    if (this.avatarUrl && this.avatarUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(this.avatarUrl);
      } catch (error) {
        console.warn('Error revoking blob URL:', this.avatarUrl, error);
      }
    }

    this.loadUserImageServiceStatus = ServiceStatus.unset;
    this.imageUrl = null;
    this.avatarUrl = null;
    this.userPreferenceUseAvatar = null;
    this.hasProfileImage = true;
    this.initials = "";
    this.userData = null;
    this.userDataServiceStatus = ServiceStatus.unset;
    this.getStudentsServiceStatus = ServiceStatus.unset;
    this.getGuardiansServiceStatus = ServiceStatus.unset;
    this.getAlternativeContactServiceStatus = ServiceStatus.unset;
    this.alternativeContact = null;
    this.flags = [];
  }

  private async initializeComponent() {
    await this.loadUserImageData();
  }

  private async loadUserImageData() {
    let cachedImageResponse = this.userImageService.loadCachedUserImageResponse(this.userId);
    if (cachedImageResponse) {
      this.setUserImageResponse(cachedImageResponse, true);
      this.cdr.markForCheck(); // Trigger change detection for cached data
    }

    this.loadUserImageServiceStatus = ServiceStatus.loading;
    this.imageLoadSubscription = this.userImageService.getUserImageAndAvatar(this.userId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (result: UserImageResponse | null) => {
        if (result) {
          this.setUserImageResponse(result);
        }
        this.loadUserImageServiceStatus = ServiceStatus.loaded;
        this.cdr.markForCheck(); // Trigger change detection after data loads
      },
      error: (error) => {
        console.error('Error loading user image and avatar:', error);
        this.loadUserImageServiceStatus = ServiceStatus.error;
        this.cdr.markForCheck(); // Trigger change detection for error state
      }
    });
  }

  private setUserImageResponse(result: UserImageResponse, _: boolean = false): void {
    this.imageUrl = this.userImageService.loadImageUrlFromBlob(result);
    this.avatarUrl = result.AvatarUrl;
    // After an explicit refresh (e.g. a freshly uploaded photo or changed avatar), append a
    // one-time cache-buster to http(s) URLs (Azure SecureUrl) so the browser/native webview
    // reloads the replaced image instead of serving the cached bytes for the same URL.
    // Blob URLs are already unique per fetch, and normal renders stay cacheable.
    if (this.forceFreshImageUrl) {
      this.imageUrl = this.appendCacheBuster(this.imageUrl);
      this.avatarUrl = this.appendCacheBuster(this.avatarUrl);
      this.forceFreshImageUrl = false;
    }
    this.userPreferenceUseAvatar = result.UserPreferenceUseAvatar;
    this.hasProfileImage = result.HasProfileImage ?? true;
    this.initials = result.Initials ?? "";
  }

  /** Appends a one-time cache-buster to an http(s) URL. Blob/empty URLs are returned unchanged. */
  private appendCacheBuster(url: string | null): string | null {
    if (!url || !/^https?:/i.test(url)) return url;
    return url + (url.includes('?') ? '&' : '?') + '_cb=' + Date.now();
  }

  private readonly INITIALS_COLORS = ['#77b800','#fe6401','#00aeef','#8e9697','#DD1C12','#52647D'];

  get initialsColor(): string {
    return this.INITIALS_COLORS[Math.abs(this.userId) % this.INITIALS_COLORS.length];
  }

  get showInitials(): boolean {
    if (this.forceShowAvatar && this.avatarUrl) return false;
    return !this.hasProfileImage;
  }

  get profileUrl(): string | null {
    if (this.showInitials) return null;

    // Force flags take absolute precedence over user preferences
    if (this.forceShowAvatar) {
      return this.avatarUrl || this.imageUrl || null;
    }

    // Guardians only ever see profile image
    if (this.forceShowProfileImage || this.currentUserType === 'Guardian') {
      return this.imageUrl || null;
    }

    // User preference for avatar
    if (this.userPreferenceUseAvatar) {
      return this.avatarUrl || this.imageUrl || null;
    }

    // Default behavior: prefer image, fallback to avatar
    return this.imageUrl || this.avatarUrl || null;
  }

  /**
   * method to refresh the component data
   * This can be called from parent components to force a refresh
   */
  async refreshUserImageData(): Promise<void> {
    // Drop the in-memory cache so the reload actually re-fetches from the server
    // (otherwise getUserImageAndAvatar short-circuits and returns the stale cached value),
    // and bust the browser/webview HTTP cache so a replaced SecureUrl image is shown.
    this.userImageService.invalidateUserImageCache(this.userId);
    this.forceFreshImageUrl = true;
    await this.loadUserImageData();
  }

  handleImageError(event: any): void {
    console.error('Error loading image for user:', this.userId, event);
    this.loadUserImageServiceStatus = ServiceStatus.error;
  }

  // #endregion

  // #region Modal Bio Helpers
  private loadData(showModalAfterLoad: boolean = false): void {
    this.userDataServiceStatus = ServiceStatus.loading;

    this.userImageService.getUser(this.displayUserId, this.schoolId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (result: MiniUserProfile) => {
        this.userData = result;

        this.userDataServiceStatus = ServiceStatus.loaded;

        if (this.userData?.AlternativeContactId) {
          this.getAlternativeContactProfile();
        }

        if (this.userData?.UserType === 'Guardian') {
          this.getStudentsForGuardian();
        }

        if (this.userData?.UserType === 'Student') {
          this.getGuardiansForStudent();
        }

        // Show modal if requested after data is loaded
        if (showModalAfterLoad) {
          this.showModal();
        }

        this.canShowEmail = this.computeCanShow(this.userData);
        this.canShowPhone = this.computeCanShow(this.userData);

      },
      error: (error) => {
        console.error('Error loading user data for modal:', error);
        this.userDataServiceStatus = ServiceStatus.error;
      }
    });
  }

  private getAlternativeContactProfile() {
    if (!this.userData?.AlternativeContactId) return;

    this.getAlternativeContactServiceStatus = ServiceStatus.loading;
    this.profileService.getUserProfileByUserIdAsync(this.userData.AlternativeContactId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (result) => {
        this.alternativeContact = result;
        this.getAlternativeContactServiceStatus = ServiceStatus.loaded
      },
      error: (error) => {
        console.error('Error loading alternative contact profile:', error);
        this.alternativeContact = null;
        this.getAlternativeContactServiceStatus = ServiceStatus.error
      }
    });
  }

  getStudentsForGuardian() {
    if (!this.userData?.GuardianId) return;

    this.getStudentsServiceStatus = ServiceStatus.loading;
    this.profileService.getGuardianStudentDtoByGuardianIdAsync(this.userData.GuardianId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (results) => {
        if (this.userData) {
          this.userData.Students = results;
        }
        this.getStudentsServiceStatus = ServiceStatus.loaded;
      },
      error: (error) => {
        console.error('Error loading students for guardian:', error);
        if (this.userData) {
          this.userData.Students = [];
        }
        this.getStudentsServiceStatus = ServiceStatus.error;
      }
    });
  }

  getGuardiansForStudent() {
    if (!this.userData?.StudentId) return;

    this.getGuardiansServiceStatus = ServiceStatus.loading;
    this.profileService.getGuardianStudentDtoByStudentIdAsync(this.userData.StudentId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (results) => {
        if (this.userData) {
          this.userData.Guardians = results;
        }
        this.getGuardiansServiceStatus = ServiceStatus.loaded;
      },
      error: (error) => {
        console.error('Error loading guardians for student:', error);
        if (this.userData) {
          this.userData.Students = [];
        }
        this.getGuardiansServiceStatus = ServiceStatus.error;
      }
    });
  } 

  getUserTitle(): string {
    if (!this.userData) return '';

    if (this.userData.UserType === 'Staff') {
      return this.userData.Title;
    }

    if (this.userData.UserType === 'Guardian') {
      return 'Parent';
    }

    return this.userData.UserType;
  }

  routeToMessages(userId: number): void {
    this.closePopup();
    // Check if current user can message this user
    if (!this.canMessageUser()) {
      // If not allowed, route to messages page without recipient
      this.router.navigateByUrl('/apps/messages/new', {
        state: { returnUrl: window.location.pathname }
      });
      return;
    }

    this.router.navigateByUrl(`/apps/messages/new/${userId}`, {
      state: { returnUrl: window.location.pathname }
    });
  }

  private showModal(): void {
    // Ensure templates are available before showing modal
    if (!this.profileDialogTemplate) {
      console.warn('Modal templates not ready, deferring modal display');
      setTimeout(() => this.showModal(), 100);
      return;
    }

    // Close any existing dialog first
    this.profileDialogRef?.close();

    // Open via the shared adaptive dialog (Material) so it lives in the same
    // overlay stack as other app dialogs (e.g. opened from the group members modal).
    this.profileDialogRef = this.adaptiveContainer.openTemplate(this.profileDialogTemplate, {
      width: '500px',
      panelClass: 'user-profile-dialog',
    });

    this.profileDialogRef.afterClosed().then(() => {
      this.profileDialogRef = undefined;
    });
  }

  clickImage(): void {
    if (this.profileEnabled) {
      this.displayUserId = this.userId;
      this.loadData(true);
    }
  }

  openAltContactCard(userId: number): void {
    this.displayUserId = userId;
    this.userData = null;
    this.alternativeContact = null;
    this.userDataServiceStatus = ServiceStatus.unset;
    this.getAlternativeContactServiceStatus = ServiceStatus.unset;
    this.loadData(false);
  }
  
  closePopup(): void {
    this.profileDialogRef?.close();
    this.profileDialogRef = undefined;
  }

  stopPropagation(event: any): void {
    if (this.profileEnabled) event.stopPropagation();
  }

  copyToClipboard(text: string): void {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(err => {
        console.error('Failed to copy: ', err);
      });
    }

    this.emailCopied = true;
    setTimeout(() => {
      this.emailCopied = false;
    }, 3000);
  }

  /**Format phone number as (xxx) xxx-xxxx if it's 10 digits*/
  formatPhoneNumber(phone: string): string {
    if (!phone) return phone;

    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // If exactly 10 digits, format as (xxx) xxx-xxxx
    if (digits.length === 10) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }

    // Otherwise, return the original phone number
    return phone;
  }

  flagUser(): void {
    this.flagStatus = ServiceStatus.loading;
    if (this.userData) {
      this.userImageService.flagUser(this.userData.Id, this.flagForm.get('CreateTicket')?.value, this.flagForm.get('Notes')?.value)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (_: CreateFlagResponse) => {
        this.flagStatus = ServiceStatus.loaded;
        this.flagFormDialogRef?.close();
        this.resetComponentState();
        this.loadData();
          },
          error: (error) => {
            console.error('Error flagging user:', error);
        this.flagStatus = ServiceStatus.error;
          }
        });
    }
  }

  getFlags(): void {
    this.flagStatus = ServiceStatus.loading;
    if (this.userData) {
      this.userImageService.getUserFlags(this.userData.Id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result: UserImageFlag[]) => {
        this.flags = result;
        this.flagStatus = ServiceStatus.loaded;
          },
          error: (error) => {
        console.error('Error loading user flags:', error);
        this.flagStatus = ServiceStatus.error;
          }
        });
    }
  }

  openViewFlagsModal(): void {
    this.dialog.open(this.flagHistoryDialog, { width: '800px', maxWidth: '90vw' });
    this.getFlags();
  }

  openFormModal(): void {
    // this.resetForm();
    this.flagFormDialogRef = this.dialog.open(this.flagFormDialog, { width: '300px', maxWidth: '90vw' });
  }

  private resetForm(): void {
    this.flagForm = this.formBuilder.group({
      Notes: [undefined, Validators.maxLength(255)],
      CreateTicket: [true]
    });
  }

  get isStaff(): boolean {
    return this.userData != null && this.userData.UserType === 'Staff';
  }

  get isGuardian(): boolean {
    return this.userData != null && this.userData.UserType === 'Guardian';
  }

  get isStudent(): boolean {
    return this.userData != null && this.userData.UserType === 'Student';
  }
  //#endregion

  // #region Online Status Helpers
  isUserOnline(): boolean {
    return this.userPresenceService.getUserPresenceById(this.userId)?.IsOnline || false;
  }

  mapStatusDotSize(): number {
    const imageSize = this.sizeAsNumber || 100;
    // Scale the dot based on image size: 30% of image size
    const scaledSize = Math.round(imageSize * .30);
    // Clamp between 8px and 24px
    this.statusDotSize = Math.max(8, Math.min(24, scaledSize));
    return this.statusDotSize;
  }

  private computeCanShow(userData: MiniUserProfile | null): boolean {
    if (!userData) return false;
    const isStaff = userData.UserType === 'Staff';
    const isGuardian = userData.UserType === 'Guardian';

    if (isStaff) return true;
    if (isGuardian && this.currentUser?.UserType === 'Staff') return true;
    return false;
  }

  canViewChats(): boolean {
    return this.currentUser?.Roles?.some(r => r.RoleName === 'MessagesAdmin') ?? false;
  }

  routeToAdminMessages(userId: number): void {
    this.closePopup();
    this.router.navigateByUrl(`/apps/messages/admin/${userId}`, {
      state: { viewedUserName: `${this.userData?.FirstName} ${this.userData?.LastName}` }
    });
  }

  canMessageUser(): boolean {
    if (!this.currentUser || !this.userData) return true;

    // Target user must be able to access messages (has AppAccess_Messages and no MessagesBan)
    if (!this.userData.CanAccessMessages) return false;

    // Guardians cannot message other Guardians
    if (this.currentUser.UserType === 'Guardian') {
      if (this.userData.UserType === 'Guardian') {
         return false;
      }
      if (this.userData.ShowInDirectory == false) {
        return false;
      }

    }
 
    // Check if Parent Relations
    const isParentRelations = this.currentUser.Roles?.some(r => r.RoleName === 'Parent Relations');
    if (isParentRelations) return true; // Parent Relations can message anyone

    // Check if current user is SC Staff (flag set during login on StaffProfile)
    if (!this.currentUser.StaffProfile?.IsServiceCenterStaff && this.userData.UserType !== 'Student') return true;
    // SC Staff (non-PR) cannot message Guardians or Students
    return this.userData.UserType !== 'Guardian' && this.userData.UserType !== 'Student';
  }

  // #endregion
}
